import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useBackHomePath } from "@/lib/useBackHomePath";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Send, Plus, Link as LinkIcon, FileText, Trash2, Eye, Clock, CheckCircle2, AlertCircle, Upload, ExternalLink, ListChecks } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const formatSize = (b) => {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const emptyForm = (overrides = {}) => ({
    student_id: "", subject_id: "", plan_id: null,
    title: "", instructions: "", text_body: "",
    resources: [], deadline_days: 5, ...overrides,
});

export default function TeacherAssignments() {
    const [params] = useSearchParams();
    const [items, setItems] = useState([]);
    const [students, setStudents] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [resKind, setResKind] = useState("link");  // link | quiz | file
    const [linkInput, setLinkInput] = useState({ url: "", label: "" });
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [viewing, setViewing] = useState(null);
    const fileInputRef = useRef(null);
    const backPath = useBackHomePath();

    const refresh = async () => {
        const [a, s, sb] = await Promise.all([
            api.get("/teacher/assignments"),
            api.get("/teacher/students"),
            api.get("/teacher/my-subjects"),
        ]);
        setItems(a.data); setStudents(s.data); setSubjects(sb.data);
    };
    useEffect(() => { refresh(); }, []);

    // Pre-fill from query params (e.g., when coming from a Plan)
    useEffect(() => {
        const sid = params.get("student_id"), pid = params.get("plan_id"), subj = params.get("subject_id");
        if (sid && !open) {
            setForm(emptyForm({ student_id: sid, plan_id: pid, subject_id: subj || "" }));
            setOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    const stats = useMemo(() => ({
        total: items.length,
        opened: items.filter((a) => a.status === "opened").length,
        pending: items.filter((a) => a.status === "pending" && !a.is_expired).length,
        expired: items.filter((a) => a.is_expired).length,
    }), [items]);

    const addLink = (type = "link") => {
        if (!linkInput.url.trim()) return toast.error("URL e detyrueshme");
        const url = linkInput.url.trim();
        if (!/^https?:\/\//i.test(url)) return toast.error("URL duhet të fillojë me http(s)://");
        setForm((f) => ({ ...f, resources: [...f.resources, { type, url, label: linkInput.label.trim() || url }] }));
        setLinkInput({ url: "", label: "" });
    };

    const uploadFile = async (file) => {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/lms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({
                ...f,
                resources: [...f.resources, {
                    type: "file", file_id: r.data.file_id, filename: r.data.filename,
                    size: r.data.size, label: r.data.filename,
                }],
            }));
            toast.success("Skedari u ngarkua");
        } catch (e) {
            toast.error("Ngarkimi dështoi: " + (e.response?.data?.detail || e.message));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const removeResource = (i) => setForm((f) => ({ ...f, resources: f.resources.filter((_, idx) => idx !== i) }));

    const submit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            const payload = {
                student_id: form.student_id,
                subject_id: form.subject_id || null,
                plan_id: form.plan_id || null,
                title: form.title,
                instructions: form.instructions,
                text_body: form.text_body,
                deadline_days: form.deadline_days,
                resources: form.resources.map((r) => ({ type: r.type, url: r.url || null, file_id: r.file_id || null, label: r.label || "" })),
            };
            await api.post("/teacher/assignment", payload);
            toast.success("Detyra u dërgua");
            setOpen(false); setForm(emptyForm()); refresh();
        } catch (err) {
            toast.error("Gabim: " + (err.response?.data?.detail || err.message));
        } finally { setBusy(false); }
    };

    const del = async (aid) => {
        if (!window.confirm("Të fshihet detyra?")) return;
        await api.delete(`/teacher/assignment/${aid}`);
        refresh();
    };

    const viewTracking = async (a) => {
        try {
            const r = await api.get(`/teacher/assignment/${a.id}/tracking`);
            setViewing(r.data);
        } catch (e) { toast.error("Gabim në tracking"); }
    };

    const fileHref = (file_id) => {
        const token = localStorage.getItem("token");
        return `${BACKEND}/api/lms/file/${file_id}?auth=${encodeURIComponent(token || "")}`;
    };

    const statusChip = (a) => {
        if (a.status === "opened") return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sage/10 text-sage border border-sage/30">Hapur</span>;
        if (a.is_expired) return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-terracotta/10 text-terracotta border border-terracotta/30">Skadoi</span>;
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-300">Pritur</span>;
    };

    return (
        <Layout>
            <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Detyrat e nxënësve</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Detyrat individuale</h1>
                    <p className="text-slate-600 mt-2 text-sm">Cakto detyra (lexim, video, quiz, PDF) me afat 1–30 ditë. Nxënësi dhe prindi marrin njoftime ditore deri sa hapet.</p>
                </div>
                <Button onClick={() => { setForm(emptyForm()); setOpen(true); }} className="bg-oxford hover:bg-oxfordhover text-white gap-2" data-testid="ta-new-btn">
                    <Plus className="w-4 h-4" /> Detyrë e re
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-3 mb-5">
                <div className="bg-white border border-slate-200 rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Total</div>
                    <div className="font-heading text-2xl font-black text-slate-800" data-testid="ta-stat-total">{stats.total}</div>
                </div>
                <div className="bg-white border border-amber-200 rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Pritur</div>
                    <div className="font-heading text-2xl font-black text-amber-700" data-testid="ta-stat-pending">{stats.pending}</div>
                </div>
                <div className="bg-white border border-sage/40 rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-sage">Hapur</div>
                    <div className="font-heading text-2xl font-black text-sage" data-testid="ta-stat-opened">{stats.opened}</div>
                </div>
                <div className="bg-white border border-terracotta/40 rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-terracotta">Skaduar</div>
                    <div className="font-heading text-2xl font-black text-terracotta" data-testid="ta-stat-expired">{stats.expired}</div>
                </div>
            </div>

            {/* List */}
            {items.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
                    <ListChecks className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                    <div className="font-heading text-xl font-bold text-slate-800">Asnjë detyrë e dërguar ende</div>
                    <p className="text-sm text-slate-600 mt-1">Klik "Detyrë e re" për të caktuar lexime, video ose quize me afat.</p>
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="divide-y divide-slate-200">
                        {items.map((a) => (
                            <div key={a.id} className="px-4 sm:px-5 py-3" data-testid={`ta-row-${a.id}`}>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-oxford/10 text-oxford flex items-center justify-center font-bold text-xs shrink-0">
                                        {a.student_name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-slate-900 text-sm truncate flex items-center gap-2 flex-wrap">
                                            {a.title} {statusChip(a)}
                                        </div>
                                        <div className="text-[11px] text-slate-500 truncate">
                                            {a.student_name} {a.subject_name && `• ${a.subject_name}`}
                                            {!a.is_expired && a.status !== "opened" && ` • ${a.days_remaining} ditë`}
                                            {a.resource_clicks > 0 && ` • ${a.resource_clicks} klikime`}
                                        </div>
                                    </div>
                                    <button onClick={() => viewTracking(a)} className="p-2 rounded-md text-oxford hover:bg-oxford/10" title="Shiko" data-testid={`ta-view-${a.id}`}><Eye className="w-4 h-4" /></button>
                                    <button onClick={() => del(a.id)} className="p-2 rounded-md text-red-500 hover:bg-red-50" title="Fshi" data-testid={`ta-del-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* New assignment dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading">Cakto detyrë për nxënësin</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        <select required value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-3" data-testid="ta-form-student">
                            <option value="">— Nxënësi —</option>
                            {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <select value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-3" data-testid="ta-form-subject">
                            <option value="">— Lënda (opsionale) —</option>
                            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <Input required placeholder="Titulli — p.sh., Lexim aktiv & quiz vlerësues" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ta-form-title" />
                        <Textarea placeholder="Udhëzime për nxënësin (opsionale)" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={2} data-testid="ta-form-instructions" />
                        <Textarea placeholder="Materiali për lexim (opsionale) — nxënësi mund ta lexojë drejtpërdrejt" value={form.text_body} onChange={(e) => setForm({ ...form, text_body: e.target.value })} rows={4} data-testid="ta-form-text-body" />
                        <div className="grid grid-cols-2 gap-2 items-center">
                            <label className="text-xs font-bold text-slate-600">Afati (ditë)</label>
                            <Input type="number" min={1} max={30} value={form.deadline_days} onChange={(e) => setForm({ ...form, deadline_days: parseInt(e.target.value) || 5 })} data-testid="ta-form-deadline" />
                        </div>

                        <div className="border-t border-slate-200 pt-3">
                            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-2 flex items-center gap-1.5">
                                <Send className="w-3 h-3" /> Resurse të bashkangjitura
                            </label>
                            {form.resources.length > 0 && (
                                <div className="space-y-2 mb-3">
                                    {form.resources.map((r, i) => (
                                        <div key={i} className="flex items-center gap-2 px-3 py-2 bg-bone/40 rounded-md border border-slate-200">
                                            {r.type === "file" ? <FileText className="w-4 h-4 text-sage shrink-0" /> : r.type === "quiz" ? <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0" /> : <LinkIcon className="w-4 h-4 text-oxford shrink-0" />}
                                            <div className="flex-1 min-w-0 text-sm">
                                                <div className="font-semibold text-slate-800 truncate">{r.label || r.filename || r.url}</div>
                                                <div className="text-[11px] text-slate-500 truncate">{r.type === "file" ? `${r.filename || "File"}${r.size ? ` • ${formatSize(r.size)}` : ""}` : r.url}</div>
                                            </div>
                                            <button type="button" onClick={() => removeResource(i)} className="p-1.5 rounded-md text-red-500 hover:bg-red-50">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Tabs: link / quiz / file */}
                            <div className="flex gap-1 mb-2">
                                {[
                                    { k: "link", label: "Link", icon: LinkIcon },
                                    { k: "quiz", label: "Quiz", icon: CheckCircle2 },
                                    { k: "file", label: "Skedar", icon: Upload },
                                ].map((t) => (
                                    <button type="button" key={t.k} onClick={() => setResKind(t.k)} className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-bold border transition flex items-center justify-center gap-1 ${resKind === t.k ? "border-oxford bg-oxford text-white" : "border-slate-200 bg-white text-slate-600 hover:border-oxford/40"}`} data-testid={`ta-res-tab-${t.k}`}>
                                        <t.icon className="w-3 h-3" /> {t.label}
                                    </button>
                                ))}
                            </div>
                            {resKind === "file" ? (
                                <div className="p-2.5 bg-bone/30 rounded-md border border-slate-200">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                                        onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
                                        disabled={uploading}
                                        className="text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-slate-300 file:bg-white file:text-xs file:font-bold file:text-slate-700 hover:file:bg-slate-50 file:cursor-pointer"
                                        data-testid="ta-file-input"
                                    />
                                    {uploading && <div className="text-[11px] text-slate-500 mt-1">Duke ngarkuar…</div>}
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="grid grid-cols-[1fr_auto] gap-2">
                                        <Input value={linkInput.url} onChange={(e) => setLinkInput({ ...linkInput, url: e.target.value })} placeholder={resKind === "quiz" ? "Link i quizit (Google Forms, Quizizz...)" : "https://..."} data-testid="ta-link-url" />
                                        <Button type="button" onClick={() => addLink(resKind)} size="sm" variant="outline" className="gap-1" data-testid="ta-link-add"><Plus className="w-3 h-3" /> Shto</Button>
                                    </div>
                                    <Input value={linkInput.label} onChange={(e) => setLinkInput({ ...linkInput, label: e.target.value })} placeholder="Titulli (opsionale)" data-testid="ta-link-label" />
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button type="submit" disabled={busy || uploading} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="ta-form-submit">
                                {busy ? "Duke dërguar…" : "Dërgo detyrën"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Tracking modal */}
            {viewing && (
                <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setViewing(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full max-h-[88vh] overflow-y-auto my-8" data-testid="ta-tracking">
                        <div className="px-5 py-3 border-b border-slate-200">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-oxford">Tracking</div>
                            <h3 className="font-heading text-xl font-bold text-slate-900">{viewing.title}</h3>
                            <div className="text-xs text-slate-500 mt-1">{viewing.student_name} {viewing.subject_name && `• ${viewing.subject_name}`}</div>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="grid grid-cols-3 gap-2">
                                <div className={`rounded-md p-2.5 border-2 ${viewing.opened_at ? "border-sage/40 bg-sage/5" : "border-slate-200"}`}>
                                    <div className="text-[10px] uppercase font-bold text-slate-500">Hapur</div>
                                    {viewing.opened_at ? (
                                        <div className="text-xs text-sage font-bold mt-0.5">{viewing.opened_at.slice(0, 16).replace("T", " ")}</div>
                                    ) : (
                                        <div className="text-xs text-slate-400 mt-0.5">Ende jo</div>
                                    )}
                                </div>
                                <div className="rounded-md p-2.5 border-2 border-slate-200">
                                    <div className="text-[10px] uppercase font-bold text-slate-500">Klikime në skedar</div>
                                    <div className="text-xs font-bold mt-0.5">{(viewing.events || []).filter((e) => e.kind === "file_click").length}</div>
                                </div>
                                <div className="rounded-md p-2.5 border-2 border-slate-200">
                                    <div className="text-[10px] uppercase font-bold text-slate-500">Klikime në quiz</div>
                                    <div className="text-xs font-bold mt-0.5">{(viewing.events || []).filter((e) => e.kind === "quiz_click").length}</div>
                                </div>
                            </div>

                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Resurset</div>
                                <div className="space-y-1.5">
                                    {(viewing.resources || []).length === 0 ? (
                                        <div className="text-xs text-slate-400">— pa resurse —</div>
                                    ) : (
                                        (viewing.resources || []).map((r, i) => {
                                            const clicks = (viewing.events || []).filter((e) => e.resource_index === i).length;
                                            return (
                                                <div key={i} className="flex items-center gap-2 text-xs">
                                                    {r.type === "file" ? <FileText className="w-3.5 h-3.5 text-sage" /> : r.type === "quiz" ? <CheckCircle2 className="w-3.5 h-3.5 text-amber-600" /> : <LinkIcon className="w-3.5 h-3.5 text-oxford" />}
                                                    <span className="flex-1 truncate font-semibold text-slate-700">{r.label || r.filename || r.url}</span>
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${clicks > 0 ? "bg-sage/10 text-sage" : "bg-slate-100 text-slate-500"}`}>{clicks} klikime</span>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Reminders</div>
                                <div className="text-xs text-slate-600">Dërguar: <strong>{viewing.reminders_sent || 0}</strong>{viewing.last_reminder_at && <> • i fundit: {viewing.last_reminder_at.slice(0, 16).replace("T", " ")}</>}</div>
                            </div>

                            <Button variant="outline" onClick={() => setViewing(null)} className="w-full">Mbyll</Button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
